import {
  Box,
  BoxProps,
  Button,
  createIcon,
  Divider,
  Heading,
  List,
  ListItem,
  ListItemProps,
  SimpleGrid,
  Tag,
  Text,
  useColorModeValue as mode,
} from '@chakra-ui/react'
import * as React from 'react'
import { FaArrowRight } from 'react-icons/fa'

const CheckIcon = createIcon({
  viewBox: '0 0 17 12',
  d: 'M0 5.82857L1.64571 4.11429L5.48571 7.2L14.8114 0L16.4571 1.71429L5.48571 12L0 5.82857Z',
})

const PricingDetail = (props: ListItemProps & { iconColor: string }) => {
  const { children, iconColor, ...rest } = props
  return (
    <ListItem display="flex" alignItems="flex-start" fontWeight="medium" {...rest}>
      <CheckIcon marginEnd="3" mt="1" color={iconColor} />
      <Text as="span" display="inline-block" width="100%">
        {children}
      </Text>
    </ListItem>
  )
}

interface PricingCardProps extends BoxProps {
  features: string[]
  name: string
  duration: string
  extras: string
  description: string
  price: string
  pricestroke: string
  discount: string
  link: string
  onClick?: () => void
  colorScheme: string
}

export const PricingCard = (props: PricingCardProps) => {
  const {
    features,
    name,
    description,
    duration,
    price,
    pricestroke,
    discount,
    extras,
    onClick,
    link,
    colorScheme: c,
    ...rest
  } = props

  return (
    <Box p={{ base: '10', md: '16' }} {...rest}>
      <Heading fontFamily="Tomato Grotesk">{name}</Heading>
      <Divider
        opacity={1}
        borderWidth="2px"
        borderColor={mode(`${c}.500`, `${c}.200`)}
        my="6"
        w="56px"
      />

      <Text fontSize="lg">
        {description}
      </Text>

      {discount.length > 0 &&
        <Tag size="md" key="md" variant="solid" colorScheme="yellow" top={{ base: '10px', lg: '15px' }} position="relative">
          {discount}
        </Tag>
      }
      <SimpleGrid
          columns={2}
          mt={0}
          alignItems="baseline"
          justifyItems="left"
          spacing={0}
          maxWidth={{ base: '100px', md: '100px' }}
          minWidth={{ base: '210px', md: '210px' }}
        >
        <Text fontSize={{ base: '4xl', md: '5xl' }} fontWeight="extrabold" maxHeight={{ base: '50px', md: '60px' }}>
          {price}
        </Text>
        <Text fontSize="2xl">
          <s>{pricestroke}</s>
        </Text>
      </SimpleGrid>

      <Box mt="0">
        <Text color={'blue.600'} fontWeight="bold" fontSize="2xl">
          {duration}
        </Text>
        <Text mt="0" color={mode('gray.600', 'gray.400')}>
          {extras}
        </Text>
      </Box>

      <Button
        my={{ base: '0.5em', md: '2em' }}
        size="md"
        mr="10px"
        pt="0.5"
        fontSize="md"
        colorScheme={c}
        as="a"
        href={link}
        target="_blank"
      >
        Enroll & start learning
      </Button>

      {name == 'Practical Data Science' &&
        <Button
          my={{ base: '0.5em', md: '2em' }}
          size="md"
          fontSize="md"
          pt="0.5"
          borderStyle="solid"
          borderWidth="1px"
          borderColor="blue.400"
          as="a"
          href="https://colab.research.google.com/github/JungleProgram/practical-data-scientist/blob/main/prepwork/prepwork.ipynb"
          target="_blank"
          rightIcon={<FaArrowRight />}
        >
          Test my Python skills
        </Button>
      }

      <Box>
        <Text fontWeight="bold" mb="2">
          What's included in the live course:
        </Text>
        <List spacing="2">
          {features.map((feature, index) => (
            <PricingDetail key={index} iconColor={mode(`${c}.500`, `${c}.200`)}>
              {feature}
            </PricingDetail>
          ))}
        </List>
      </Box>
    </Box>
  )
}